import { Alert, Box, BoxProps, Button, Card, CardProps, Divider, IconButton, Link, Typography } from "@mui/material";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { getProjectSubRouteTemplate, INTEGRATIONS_SUBROUTE, MIGRATE_OPS_SUBROUTE, PROJECT_SUBROUTE } from "../app/AppRoutes";
import { OperationRecipeID } from "gc-web-proto/galaxycompletepb/operationpb/operation_pb";
import Email from "../../assets/integration_logos/email_icon.png";
import Placeholder from "../../assets/integration-placeholder.png";
import React, { ComponentType, ElementType } from "react";
import { observer } from "mobx-react-lite";
import { IntegrationConfigInfo, IntegrationDefinition, IntegrationModule } from "gc-web-proto/galaxycompletepb/apipb/domainpb/integration_pb";
import { ActionConfig, ActionMenuButton } from "../../common/actions/CommonActions";
import DGS from "../../assets/integration_logos/dgs.png";
import Azure from "../../assets/integration_logos/azure.png";
import AWS from "../../assets/integration_logos/aws.png";
import Pure from "../../assets/integration_logos/pure.png";
import NetApp from "../../assets/integration_logos/netapp.png";
import DigitalOcean from "../../assets/integration_logos/digitalocean.png";
import GCP from "../../assets/integration_logos/gcp.png";
import Oracle from "../../assets/integration_logos/oracle.png";
import VMware from "../../assets/integration_logos/vmware.png";
import AwsFsxNetapp from "../../assets/integration_logos/aws_fsx_netapp.png";
import Silk from "../../assets/integration_logos/silk.png";
import Ovirt from "../../assets/integration_logos/ovirt.png";
import Kubernetes from "../../assets/integration_logos/kubernetes.png";
import { MdArrowBack, MdCloud, MdShare, MdStorage } from "react-icons/md";
import { OperatorView } from "../auth/AuthenticatedViews";
import DellTechnologies from "../../assets/integration_logos/dell_technologies.png";
import { AzureStoragePlannerActions } from "./azureStoragePlanner/AzureStoragePlannerWizard";
import { useIntegrationRedirectState } from "./IntegrationRedirectState";
import { SiNutanix } from "react-icons/si";
import HitachiVantara from "../../assets/integration_logos/HV-Logo-White-RGB-604x235.svg";
import { TbReportAnalytics } from "react-icons/tb";
import { CreateThinDataReportButton } from "../migrateops/wizard/thinDataReport/MigrateOpsThinDataReportWizard";
import ApacheCloudstack from "../../assets/integration_logos/apache_cloudstack_image_logo.png";
import Openstack from "../../assets/integration_logos/openstack_logo_mark.png";
import VergeIo from "../../assets/integration_logos/verge-io.png";
import Infinidat from "../../assets/integration_logos/infinidat.svg";
import Proxmox from "../../assets/integration_logos/proxmox.svg";
import Zadara from "../../assets/integration_logos/zadara.svg";
import { MdAdd } from "react-icons/md";
import { SelectableBox } from "../../common/card/SelectableCard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";

export enum INTEGRATIONS_TYPE_SUBROUTE {
    AWS = "aws",
    AZURE = "azure",
    DGS = "dgs",
    PURE_CBS = "pure-cbs",
    PURE_AZURE = "pureazure",
    EMAIL = "email",
    NETAPP = "netapp",
    GOOGLE = "google-cloud-platform",
    DIGITAL_OCEAN = "digital-ocean",
    ORACLE = "oracle",
    PURE_FLASH = "pure-flash-array",
    POWERSTORE = "dell-emc-powerstore",
    FC_POWERSTORE = "dell-emc-fc-powerstore",
    POWERMAX = "dell-emc-powermax",
    FC_POWERMAX = "dell-emc-fc-powermax",
    AWS_FSX_NETAPP = "aws-fsx-netapp",
    SILK = "silk",
    POWERFLEX = "dell-emc-powerflex",
    ELASTIC_SAN = "azure-elastic-san",
    VMWARE_DATASTORE = "vmware-datastore",
    NUTANIX = "nutanix",
    OVIRT = "ovirt",
    KUBERNETES = "kubernetes",
    APACHE_CLOUDSTACK = "apache-cloudstack",
    OPENSTACK = "openstack",
    VERGE_IO = "verge-io",
    FC_INFINIBOX = "fc-infinibox",
    PROXMOX = "proxmox",
    NATIVEEDGE = "nativeedge",
    HITACHI_VOSB = "hitachi-vosb",
    ZADARA = "zadara",
    //NVME_POWERMAX = "dell-emc-nvme-powermax",
    NFS_GENERIC = "nfs-generic",
    SMB_GENERIC = "smb-generic",
    S3_GENERIC = "s3-generic",
}

const smallIconStyles = {
    width: 30,
    height: 30,
};

const catalogIconStyles = {
    width: 36,
    height: 36,
};

const normalIconStyles = {
    width: 100,
    height: 100,
};

const integrationImageIconStyle = {
    objectFit: "contain" as const,
    display: "block",
    maxWidth: "100%",
    maxHeight: "100%",
};

interface IntegrationIconProps {
    size?: "small" | "catalog" | "large";
    customIconWidthHeightRatio?: number;
}

interface IntegrationImageIconProps extends IntegrationIconProps {
    src: string;
    altKey: string;
    widthHeightRatio?: number;
}

const getIntegrationImageIconProps = (size?: "small" | "catalog" | "large", widthHeightRatio = 1) => {
    const iconSize = size === "small" ? smallIconStyles : size === "catalog" ? catalogIconStyles : normalIconStyles;

    return {
        width: iconSize.width * widthHeightRatio,
        height: iconSize.height,
        style: integrationImageIconStyle,
    };
};

const IntegrationImageIcon: React.FC<IntegrationImageIconProps> = ({ src, altKey, size, widthHeightRatio }) => {
    const { t } = useTranslation("integrations");
    return <img src={src} alt={t(altKey)} {...getIntegrationImageIconProps(size, widthHeightRatio)} />;
};

export const EmailIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Email} altKey={"cards.icons.email"} size={p.size} />;
};

export const PlaceholderIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Placeholder} altKey={"cards.icons.generic"} size={p.size} />;
};

export const AzureIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Azure} altKey={"cards.icons.azure"} size={p.size} />;
};

export const DgsIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={DGS} altKey={"cards.icons.cirrusDataCloud"} size={p.size} />;
};

export const AwsIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={AWS} altKey={"cards.icons.aws"} size={p.size} />;
};

export const PureIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Pure} altKey={"cards.icons.pureStorage"} size={p.size} />;
};

export const EverpureIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Pure} altKey={"cards.icons.everpureAzure"} size={p.size} />;
};

export const NetAppIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={NetApp} altKey={"cards.icons.netapp"} size={p.size} />;
};

export const GCPIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={GCP} altKey={"cards.icons.googleCloud"} size={p.size} />;
};

export const DigitalOceanIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={DigitalOcean} altKey={"cards.icons.digitalOcean"} size={p.size} />;
};

export const OracleIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Oracle} altKey={"cards.icons.oracle"} size={p.size} />;
};

export const DellEmcIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={DellTechnologies} altKey={"cards.icons.dellEmc"} size={p.size} />;
};

export const VmwareIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={VMware} altKey={"cards.icons.vmware"} size={p.size} />;
};

export const AwsFsxNetappIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={AwsFsxNetapp} altKey={"cards.icons.awsFsxNetapp"} size={p.size} />;
};

export const SilkIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Silk} altKey={"cards.icons.silk"} size={p.size} />;
};

export const AzureElasticSanIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Azure} altKey={"cards.icons.azureElasticSan"} size={p.size} />;
};

export const AzureStoragePlannerIcon: React.FC<IntegrationIconProps> = (p) => {
    return <IntegrationImageIcon src={Azure} altKey={"cards.icons.azureStoragePlanner"} size={p.size} />;
};

export const NutanixIcon: React.FC<IntegrationIconProps> = (p) => {
    const iconSize = p.size === "small" ? smallIconStyles.width : p.size === "catalog" ? catalogIconStyles.width : normalIconStyles.width;
    return <SiNutanix size={iconSize} />;
};

export const ThinDataReportIcon: React.FC<IntegrationIconProps> = (p) => {
    const iconSize = p.size === "small" ? smallIconStyles.width : p.size === "catalog" ? catalogIconStyles.width : normalIconStyles.width;
    return <TbReportAnalytics size={iconSize} />;
};

export const OvirtIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Ovirt} alt={"Ovirt"} {...getIntegrationImageIconProps(p.size)} />;
};

export const KubernetesIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Kubernetes} alt={"Kubernetes"} {...getIntegrationImageIconProps(p.size)} />;
};

export const ApacheCloudstackIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={ApacheCloudstack} alt={"Apache Cloudstack"} {...getIntegrationImageIconProps(p.size, p.customIconWidthHeightRatio)} />;
};

export const OpenstackIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Openstack} alt={"Openstack"} {...getIntegrationImageIconProps(p.size, p.customIconWidthHeightRatio)} />;
};

export const VergeIoIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={VergeIo} alt={"Verge.io"} {...getIntegrationImageIconProps(p.size)} />;
};

export const ProxmoxIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Proxmox} alt={"Proxmox"} {...getIntegrationImageIconProps(p.size)} />;
};

export const InfinidatIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Infinidat} alt={"Infinidat"} {...getIntegrationImageIconProps(p.size)} />;
};

export const HitachiIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={HitachiVantara} alt={"Hitachi Vantara"} {...getIntegrationImageIconProps(p.size, p.customIconWidthHeightRatio)} />;
};

export const ZadaraIcon: React.FC<IntegrationIconProps> = (p) => {
    return <img src={Zadara} alt={"Zadara"} {...getIntegrationImageIconProps(p.size)} />;
};

export const NfsGenericIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    const iconSize = p.size === "small" ? smallIconStyles.width : p.size === "catalog" ? catalogIconStyles.width : normalIconStyles.width;
    return <MdStorage size={iconSize} />;
};

export const SmbGenericIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    const iconSize = p.size === "small" ? smallIconStyles.width : p.size === "catalog" ? catalogIconStyles.width : normalIconStyles.width;
    return <MdShare size={iconSize} />;
};

export const S3GenericIntegrationIcon: React.FC<IntegrationIconProps> = (p) => {
    const iconSize = p.size === "small" ? smallIconStyles.width : p.size === "catalog" ? catalogIconStyles.width : normalIconStyles.width;
    return <MdCloud size={iconSize} />;
};

type ModuleIconProps<P = {}> = {
    as?: ComponentType<P> | ElementType;
} & P;

export const ModuleIcon = <P,>({ as: Component = "div", ...props }: ModuleIconProps<P>) => {
    return <Component {...props} />;
};

export enum IntegrationCategory {
    ALERT = 0,
    PUBLIC_CLOUD = 1,
    ISCSI_STORAGE_ARRAY = 2,
    FC_STORAGE_ARRAY = 3,
    COMPUTE_INTEGRATION = 4,
    HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE = 5,
    PUBLIC_CLOUD_STORAGE_PLANNER = 6,
    FILE_STORAGE = 7,
    CONTAINERS = 8,
    S3 = 9,
    //NVME_STORAGE_ARRAY = 10,
}

export const getIntegrationCategoryDisplayName = (i: IntegrationCategory) => {
    if (i === IntegrationCategory.ALERT) {
        return appI18n.t("integrations:categories.alert");
    } else if (i === IntegrationCategory.ISCSI_STORAGE_ARRAY) {
        return appI18n.t("integrations:categories.iscsiStorageArray");
    } else if (i === IntegrationCategory.PUBLIC_CLOUD) {
        return appI18n.t("integrations:categories.publicCloud");
    } else if (i === IntegrationCategory.FC_STORAGE_ARRAY) {
        return appI18n.t("integrations:categories.fcStorageArray");
    } else if (i === IntegrationCategory.COMPUTE_INTEGRATION) {
        return appI18n.t("integrations:categories.computeIntegration");
    } else if (i === IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE) {
        return appI18n.t("integrations:categories.hypervisorAndSoftwareDefinedStorage");
    } else if (i === IntegrationCategory.PUBLIC_CLOUD_STORAGE_PLANNER) {
        return appI18n.t("integrations:categories.storageConsumptionPlanner");
    } else if (i === IntegrationCategory.FILE_STORAGE) {
        return appI18n.t("integrations:categories.fileStorage");
    } else if (i === IntegrationCategory.CONTAINERS) {
        return appI18n.t("integrations:categories.containers");
    } else if (i === IntegrationCategory.S3) {
        return "S3";
        // } else if (i === IntegrationCategory.NVME_STORAGE_ARRAY) {
        //     return "NVMe/TCP Storage Array";
    }
};

export enum IntegrationNonModuleId {
    AZURE_STORAGE_PLANNER = "azure-storage-planner",
    SOURCE_DATA_ASSESSMENT = "source-data-assessment",
}

export interface IntegrationModuleConfig {
    module?: IntegrationModule;
    moduleDefinition?: IntegrationDefinition.AsObject;
    label?: string;
    route?: INTEGRATIONS_TYPE_SUBROUTE;
    category: IntegrationCategory;
    icon: ComponentType<any> | ElementType;
    defaultEnabled?: boolean;
    disabled?: boolean;
    externalLink?: string;
    mainActionButtonLabel?: string;
    mainActionButton?: React.ReactNode;
    description?: string;
    nonModuleId?: IntegrationNonModuleId;
    nonModuleRoute?: string;
    customIconWidthHeightRatio?: number;
}

export const getModuleConfigByModule = (module: IntegrationModule, integrationDefs?: IntegrationDefinition.AsObject[]) => {
    const defs = getIntegrationModuleConfigsWithDefinitions(integrationDefs);
    return defs.find((d) => d.module === module);
};

export const getIntegrationModuleConfigsWithDefinitions = (integrationDefs?: IntegrationDefinition.AsObject[]): IntegrationModuleConfig[] => {
    const moduleConfigs = getIntegrationModuleConfigs();
    return moduleConfigs.map((moduleConfig) => {
        const integrationDef = integrationDefs?.find((i) => moduleConfig.module === i.module);
        return {
            ...moduleConfig,
            moduleDefinition: integrationDef,
        };
    });
};

export const getIntegrationModuleConfigs = (): IntegrationModuleConfig[] => [
    {
        module: IntegrationModule.AWS,
        route: INTEGRATIONS_TYPE_SUBROUTE.AWS,
        category: IntegrationCategory.PUBLIC_CLOUD,
        icon: AwsIntegrationIcon,
    },
    {
        module: IntegrationModule.AZURE,
        route: INTEGRATIONS_TYPE_SUBROUTE.AZURE,
        category: IntegrationCategory.PUBLIC_CLOUD,
        icon: AzureIntegrationIcon,
    },
    {
        label: "Microsoft Azure",
        icon: AzureStoragePlannerIcon,
        description: appI18n.t("integrations:staticIntegrations.azurePlannerDescription"),
        category: IntegrationCategory.PUBLIC_CLOUD_STORAGE_PLANNER,
        mainActionButton: <AzureStoragePlannerActions />,
        nonModuleId: IntegrationNonModuleId.AZURE_STORAGE_PLANNER,
        nonModuleRoute: `${PROJECT_SUBROUTE.INTEGRATIONS}/${INTEGRATIONS_SUBROUTE.AZURE_STORAGE_PLANNER}`,
    },
    {
        module: IntegrationModule.AZURESAN,
        route: INTEGRATIONS_TYPE_SUBROUTE.ELASTIC_SAN,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: AzureElasticSanIntegrationIcon,
    },
    {
        module: IntegrationModule.DGS,
        route: INTEGRATIONS_TYPE_SUBROUTE.DGS,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: DgsIntegrationIcon,
    },
    {
        module: IntegrationModule.PURE,
        route: INTEGRATIONS_TYPE_SUBROUTE.PURE_CBS,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: PureIntegrationIcon,
    },
    {
        module: IntegrationModule.PUREAZURE,
        label: "Everpure Cloud Azure Native",
        route: INTEGRATIONS_TYPE_SUBROUTE.PURE_AZURE,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: EverpureIntegrationIcon,
        description: appI18n.t("integrations:staticIntegrations.everpureAzureDescription"),
    },
    {
        module: IntegrationModule.NETAPP,
        route: INTEGRATIONS_TYPE_SUBROUTE.NETAPP,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: NetAppIntegrationIcon,
    },
    {
        module: IntegrationModule.GCP,
        route: INTEGRATIONS_TYPE_SUBROUTE.GOOGLE,
        category: IntegrationCategory.PUBLIC_CLOUD,
        icon: GCPIntegrationIcon,
    },
    {
        module: IntegrationModule.ORACLE,
        route: INTEGRATIONS_TYPE_SUBROUTE.ORACLE,
        category: IntegrationCategory.PUBLIC_CLOUD,
        icon: OracleIntegrationIcon,
    },
    {
        route: INTEGRATIONS_TYPE_SUBROUTE.DIGITAL_OCEAN,
        category: IntegrationCategory.PUBLIC_CLOUD,
        icon: DigitalOceanIntegrationIcon,
        module: IntegrationModule.DIGITALOCEAN,
    },
    {
        label: appI18n.t("integrations:staticIntegrations.emailLabel"),
        route: INTEGRATIONS_TYPE_SUBROUTE.EMAIL,
        category: IntegrationCategory.ALERT,
        icon: EmailIcon,
        description: appI18n.t("integrations:staticIntegrations.emailDescription"),
        defaultEnabled: true,
    },
    {
        module: IntegrationModule.FC_PURE,
        route: INTEGRATIONS_TYPE_SUBROUTE.PURE_FLASH,
        category: IntegrationCategory.FC_STORAGE_ARRAY,
        icon: PureIntegrationIcon,
    },
    {
        module: IntegrationModule.POWERSTORE,
        route: INTEGRATIONS_TYPE_SUBROUTE.POWERSTORE,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: DellEmcIntegrationIcon,
    },
    {
        module: IntegrationModule.FC_POWERSTORE,
        route: INTEGRATIONS_TYPE_SUBROUTE.FC_POWERSTORE,
        category: IntegrationCategory.FC_STORAGE_ARRAY,
        icon: DellEmcIntegrationIcon,
    },
    {
        module: IntegrationModule.POWERMAX,
        route: INTEGRATIONS_TYPE_SUBROUTE.POWERMAX,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: DellEmcIntegrationIcon,
    },
    {
        module: IntegrationModule.FC_POWERMAX,
        route: INTEGRATIONS_TYPE_SUBROUTE.FC_POWERMAX,
        category: IntegrationCategory.FC_STORAGE_ARRAY,
        icon: DellEmcIntegrationIcon,
    },
    // {
    //     module: IntegrationModule.NVME_POWERMAX,
    //     route: INTEGRATIONS_TYPE_SUBROUTE.NVME_POWERMAX,
    //     category: IntegrationCategory.NVME_STORAGE_ARRAY,
    //     icon: DellEmcIntegrationIcon,
    // },
    {
        module: IntegrationModule.FC_INFINIBOX,
        route: INTEGRATIONS_TYPE_SUBROUTE.FC_INFINIBOX,
        category: IntegrationCategory.FC_STORAGE_ARRAY,
        icon: InfinidatIcon,
    },
    {
        module: IntegrationModule.AWS_FSX_NETAPP,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: AwsFsxNetappIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.AWS_FSX_NETAPP,
    },
    // {
    //     label: "VMware vCenter",
    //     category: IntegrationCategory.COMPUTE_INTEGRATION,
    //     icon: VmwareIntegrationIcon,
    //     description: "VMware, Inc.",
    //     externalLink: KnownArticle.CMC_HELPER_VMWARE,
    //     mainActionButtonLabel: "Deploy CMC Helper",
    // },
    {
        module: IntegrationModule.SILKSDP,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        icon: SilkIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.SILK,
    },
    {
        label: "Dell PowerFlex",
        module: IntegrationModule.POWERFLEX,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
        icon: DellEmcIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.POWERFLEX,
    },
    {
        module: IntegrationModule.VMWAREDATASTORE,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
        icon: VmwareIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.VMWARE_DATASTORE,
    },
    {
        module: IntegrationModule.NUTANIX,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
        icon: NutanixIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.NUTANIX,
    },
    {
        module: IntegrationModule.OVIRT,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
        icon: OvirtIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.OVIRT,
    },
    {
        label: appI18n.t("integrations:staticIntegrations.sourceAssessmentLabel"),
        icon: ThinDataReportIcon,
        description: appI18n.t("integrations:staticIntegrations.sourceAssessmentDescription"),
        category: IntegrationCategory.PUBLIC_CLOUD_STORAGE_PLANNER,
        mainActionButton: <CreateThinDataReportButton />,
        nonModuleId: IntegrationNonModuleId.SOURCE_DATA_ASSESSMENT,
        nonModuleRoute: `${PROJECT_SUBROUTE.MIGRATE_OPS}/${MIGRATE_OPS_SUBROUTE.WIZARD}/${OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT}`,
    },
    {
        module: IntegrationModule.KUBERNETES,
        label: "Kubernetes",
        icon: KubernetesIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.KUBERNETES,
        category: IntegrationCategory.CONTAINERS,
    },
    {
        module: IntegrationModule.CLOUDSTACK,
        icon: ApacheCloudstackIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.APACHE_CLOUDSTACK,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
        customIconWidthHeightRatio: 2.5,
    },
    {
        module: IntegrationModule.OPENSTACK,
        icon: OpenstackIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.OPENSTACK,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
    },
    {
        module: IntegrationModule.VERGEIO,
        icon: VergeIoIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.VERGE_IO,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
    },
    {
        module: IntegrationModule.PROXMOX,
        icon: ProxmoxIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.PROXMOX,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
    },
    {
        module: IntegrationModule.NATIVEEDGE,
        label: "Dell Distributed Private Cloud",
        description: appI18n.t("integrations:staticIntegrations.nativeEdgeDescription"),
        icon: DellEmcIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.NATIVEEDGE,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
    },
    {
        module: IntegrationModule.HITACHIVOSB,
        label: "Hitachi VSP One SDS Block (iSCSI)",
        description: "Hitachi Vantara",
        icon: HitachiIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.HITACHI_VOSB,
        category: IntegrationCategory.ISCSI_STORAGE_ARRAY,
        customIconWidthHeightRatio: 2.5,
    },
    {
        module: IntegrationModule.ZADARA,
        icon: ZadaraIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.ZADARA,
        category: IntegrationCategory.HYPERVISOR_HCI_SOFTWARE_DEFINED_STORAGE,
    },
    {
        module: IntegrationModule.NFS_GENERIC,
        label: "NFS",
        description: appI18n.t("integrations:staticIntegrations.nfsDescription"),
        icon: NfsGenericIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.NFS_GENERIC,
        category: IntegrationCategory.FILE_STORAGE,
    },
    {
        module: IntegrationModule.SMB_GENERIC,
        label: "SMB",
        description: appI18n.t("integrations:staticIntegrations.smbDescription"),
        icon: SmbGenericIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.SMB_GENERIC,
        category: IntegrationCategory.FILE_STORAGE,
    },
    {
        module: IntegrationModule.S3_GENERIC,
        label: "S3",
        description: appI18n.t("integrations:staticIntegrations.s3Description"),
        icon: S3GenericIntegrationIcon,
        route: INTEGRATIONS_TYPE_SUBROUTE.S3_GENERIC,
        category: IntegrationCategory.S3,
    },
];

// ======================
// IntegrationCard
// ======================

interface IntegrationCardProps {
    module: IntegrationModuleConfig;
    instance?: IntegrationConfigInfo.AsObject;
    mainActionButton?: React.ReactNode;
    cardProps?: Partial<CardProps>;
    cardActionsGetter?: (i: IntegrationConfigInfo.AsObject) => ActionConfig[];
    dividerColor?: string;
    layout?: "default" | "catalog";
    catalogClickRoute?: INTEGRATIONS_TYPE_SUBROUTE;
}

export const IntegrationCard: React.FC<IntegrationCardProps> = observer((p) => {
    const { t } = useTranslation("integrations");
    const navigate = useNavigate();
    const { projectId } = useParams();
    const goToDetails = useNavigateToIntegrationDetails(p.instance?.id);
    const goToCreateForm = useNavigateToCreateIntegrationForm(p.catalogClickRoute);
    const title = p.module.label || p.module.moduleDefinition?.name;
    const description = p.module.description || p.module.moduleDefinition?.description;
    const goToNonModuleRoute = p.module.nonModuleRoute
        ? () => navigate(generatePath(getProjectSubRouteTemplate(p.module.nonModuleRoute), { projectId }))
        : undefined;
    const catalogClickHandler = p.catalogClickRoute ? goToCreateForm : goToNonModuleRoute;
    const isCatalogClickable = p.layout === "catalog" && !!catalogClickHandler;

    if (p.layout === "catalog") {
        return (
            <SelectableBox
                selected={false}
                onSelect={isCatalogClickable ? catalogClickHandler : undefined}
                background={"light"}
                cardProps={{
                    ...p.cardProps,
                    tabIndex: isCatalogClickable ? 0 : p.cardProps?.tabIndex,
                    role: isCatalogClickable ? "link" : p.cardProps?.role,
                    sx: {
                        p: 0,
                        height: "100%",
                        overflow: "hidden",
                        ...p.cardProps?.sx,
                    },
                }}
                disabled={!isCatalogClickable}
            >
                <Box display={"flex"} alignItems={"stretch"} height={"100%"}>
                    <Box width={72} minWidth={72} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                        <ModuleIcon as={p.module.icon} size={"catalog"} customIconWidthHeightRatio={p.module.customIconWidthHeightRatio} />
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} gap={0.5} flexGrow={1} minWidth={0} pr={2} py={1.5}>
                        <Box display={"flex"} justifyContent={"space-between"} gap={1} alignItems={"flex-start"} minWidth={0}>
                            <Box minWidth={0} flexGrow={1}>
                                <Typography variant={"subtitle1"} color={"common.white"}>
                                    {title}
                                </Typography>
                            </Box>
                            {!!p.module.defaultEnabled && (
                                <Box display={"flex"} justifyContent={"flex-end"} flexShrink={0}>
                                    <CheckCircleOutlineIcon color={"success"} fontSize={"small"} titleAccess={t("cards.enabledByDefault")} />
                                </Box>
                            )}
                        </Box>
                        <Box minWidth={0}>
                            {!!description && (
                                <Typography variant={"caption"} color={"text.secondary"}>
                                    {description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </SelectableBox>
        );
    }

    return (
        <Card {...p.cardProps}>
            <Grid container direction={"column"} alignItems={"stretch"}>
                {!!p.cardActionsGetter && (
                    <OperatorView>
                        <Grid>
                            <Box display={"flex"} justifyContent={"flex-end"} pr={1} pt={1}>
                                <ActionMenuButton actions={p.cardActionsGetter(p.instance)} />
                            </Box>
                        </Grid>
                    </OperatorView>
                )}
                <Grid>
                    <Box display={"flex"} justifyContent={"center"} pb={4} pt={!!p.cardActionsGetter ? 0 : 4}>
                        <ModuleIcon as={p.module.icon} size={"large"} customIconWidthHeightRatio={p.module.customIconWidthHeightRatio} />
                    </Box>
                </Grid>
                <Grid>
                    <Divider sx={{ borderColor: p.dividerColor ?? "rgba(255, 255, 255, .2)" }} />
                </Grid>
                <Grid>
                    <Box p={2}>
                        <Box>
                            {!!p.instance && (
                                <Box pb={1}>
                                    <Typography variant={"body2"} color={"text.secondary"}>
                                        {t("cards.id", { id: p.instance.id })}
                                    </Typography>
                                    <Typography variant={"body1"}>
                                        <Link onClick={goToDetails}>{p.instance.friendlyName}</Link>
                                    </Typography>
                                </Box>
                            )}
                            <Box minHeight={100}>
                                <Typography variant={"body1"}>{title}</Typography>
                                <Typography variant={"body2"} color={"text.secondary"}>
                                    {description}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                {(!!p.mainActionButton || !!p.module.defaultEnabled) && (
                    <Grid>
                        <Box p={2} display={"flex"} justifyContent={"center"}>
                            {p.mainActionButton || (
                                <Button variant={"contained"} disabled>
                                    {t("cards.enabled")}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Card>
    );
});

// ======================
// AddIntegrationButton
// ======================

interface AddIntegrationButtonProps {
    route: INTEGRATIONS_TYPE_SUBROUTE;
    defaultEnabled: boolean;
    disabled?: boolean;
    label?: string;
    size?: "small" | "medium" | "large";
    allowMultipleInstances?: boolean;
    variant?: "text" | "outlined" | "contained";
    iconOnly?: boolean;
}

export const AddIntegrationButton: React.FC<AddIntegrationButtonProps> = observer((p) => {
    const { t } = useTranslation("integrations");
    const goToCreateForm = useNavigateToCreateIntegrationForm(p.route);
    const disableButton = p.disabled || (!p.allowMultipleInstances && p.defaultEnabled);
    const buttonLabel = !p.allowMultipleInstances && p.defaultEnabled ? t("cards.enabled") : p.label || t("cards.addIntegration");

    if (p.iconOnly) {
        return (
            <IconButton color={"primary"} size={p.size} onClick={goToCreateForm} disabled={disableButton} aria-label={buttonLabel}>
                <MdAdd />
            </IconButton>
        );
    }

    return (
        <Button variant={p.variant || "contained"} color={"primary"} onClick={goToCreateForm} disabled={disableButton} size={p.size}>
            {buttonLabel}
        </Button>
    );
});

export interface IntegrationAlertConfig {
    alertMessage: string;
    navigateBack: () => void;
    navigateBackLabel: string;
}

// ======================
// IntegrationRedirectAlert
// ======================

interface IntegrationRedirectAlertProps {
    alertConfig: IntegrationAlertConfig;
    boxProps: Partial<BoxProps>;
}

export const IntegrationRedirectAlert: React.FC<IntegrationRedirectAlertProps> = observer((p) => {
    const resetIntegrationRedirectState = useIntegrationRedirectState((s) => s.resetIntegrationRedirectState);
    const resetIntegrationRedirect = () => {
        p.alertConfig.navigateBack();
        resetIntegrationRedirectState();
    };

    if (!p.alertConfig) {
        return null;
    }

    return (
        <>
            <Box {...p.boxProps}>
                <Alert
                    severity={"info"}
                    action={
                        <Button onClick={resetIntegrationRedirect} color={"inherit"} startIcon={<MdArrowBack />} size={"small"}>
                            {p.alertConfig?.navigateBackLabel}
                        </Button>
                    }
                >
                    {p.alertConfig.alertMessage}
                </Alert>
            </Box>
        </>
    );
});

export const getIntegrationModuleFromRoute = (route: INTEGRATIONS_TYPE_SUBROUTE) => {
    return getIntegrationModuleConfigs().find((m) => m.route === route).module;
};

export const getIntegrationRouteFromModule = (module: IntegrationModule) => {
    return getIntegrationModuleConfigs().find((m) => m.module === module).route;
};

export const INTEGRATIONS_REDIRECTED_QUERY_PARAM = "redirected";

export const generateIntegrationsSubRoutePath = (subroute: string, projectId: string, query?: boolean) => {
    if (query) {
        return generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.INTEGRATIONS}?${subroute}`), { projectId });
    }
    return generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.INTEGRATIONS}/${subroute}`), { projectId });
};

export const useRedirectToIntegrations = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generateIntegrationsSubRoutePath(`${INTEGRATIONS_REDIRECTED_QUERY_PARAM}=true`, projectId, true);
    return () => navigate(p);
};

export const useNavigateToIntegrations = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.INTEGRATIONS}`), { projectId });
    return () => navigate(p);
};

export const useNavigateToEnabledIntegrations = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generateIntegrationsSubRoutePath(INTEGRATIONS_SUBROUTE.ENABLED, projectId);
    return () => navigate(p);
};

export const useNavigateToCreateIntegrationForm = (route?: INTEGRATIONS_TYPE_SUBROUTE) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return () => {
        if (!route) return;
        const subroute = `${INTEGRATIONS_SUBROUTE.CREATE}/${route}`;
        const p = generateIntegrationsSubRoutePath(subroute, projectId);
        navigate(p);
    };
};

export const useNavigateToEditIntegrationForm = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();

    return (id: string) => {
        const p = generateIntegrationsSubRoutePath(`${INTEGRATIONS_SUBROUTE.ENABLED}/${id}/edit`, projectId);
        navigate(p);
    };
};

export const useNavigateToIntegrationDetails = (id: number) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generateIntegrationsSubRoutePath(`${INTEGRATIONS_SUBROUTE.ENABLED}/${id}`, projectId);
    return () => {
        navigate(p);
    };
};
